import { useCallback, useEffect, useRef, useState } from "react";
import { IconClose } from "@humansignal/icons";
import { cn } from "../../utils/bem";
import "./EmailChipInput.prefix.css";

const block = cn("email-chip-input");

/** x@y.z — at least one dot after the @; no spaces. */
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Email token/chip input. Type or paste emails separated by comma / space /
 * semicolon / Enter; each becomes a removable chip. De-duplicates and keeps
 * only strings containing "@".
 *
 * `renderChipExtra(email)` (optional) renders inside each chip, after the
 * address — e.g. a per-person role select.
 *
 * `suggest(query)` (optional) returns a promise of `[{ email, name? }]`; while
 * typing, matches appear in a dropdown (arrow keys + Enter, or click) so people
 * who already have an account can be picked instead of retyped.
 */
export const EmailChipInput = ({ emails, onChange, placeholder, suggest, autoFocus, renderChipExtra, tall }) => {
  const [input, setInput] = useState("");
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [invalid, setInvalid] = useState([]);
  const timer = useRef();
  const seq = useRef(0);

  // Adds the valid addresses, returns the ones that are not x@y.z so the
  // caller can keep them in the input and show them.
  const addEmail = useCallback(
    (raw) => {
      const parts = raw
        .split(/[\s,;]+/)
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean);
      const good = parts.filter((e) => EMAIL_RE.test(e));
      const bad = parts.filter((e) => !EMAIL_RE.test(e));
      if (good.length) onChange([...new Set([...emails, ...good])]);
      setInvalid(bad);
      return bad;
    },
    [emails, onChange],
  );

  const commitInput = () => {
    if (!input.trim()) return;
    const bad = addEmail(input);
    setInput(bad.join(", "));
  };

  // Debounced suggestion lookup.
  useEffect(() => {
    if (!suggest) return;
    const q = input.trim();
    clearTimeout(timer.current);
    if (!q) {
      setOptions([]);
      setOpen(false);
      return;
    }
    timer.current = setTimeout(async () => {
      const id = ++seq.current;
      try {
        const res = (await suggest(q)) || [];
        if (id !== seq.current) return;
        const chosen = new Set(emails);
        const list = res.filter((o) => o?.email && !chosen.has(o.email.toLowerCase())).slice(0, 8);
        setOptions(list);
        setActive(0);
        setOpen(list.length > 0);
      } catch {
        if (id === seq.current) {
          setOptions([]);
          setOpen(false);
        }
      }
    }, 180);
    return () => clearTimeout(timer.current);
  }, [input, suggest, emails]);

  const pick = (o) => {
    addEmail(o.email);
    setInput("");
    setOptions([]);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (open && options.length) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((a) => (a + 1) % options.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((a) => (a - 1 + options.length) % options.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        pick(options[active]);
        return;
      }
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
    }
    if (["Enter", ",", " ", "Tab"].includes(e.key)) {
      if (e.key === "Tab" && !input.trim()) return; // let Tab move focus on an empty input
      e.preventDefault();
      commitInput();
    } else if (e.key === "Backspace" && !input && emails.length) {
      onChange(emails.slice(0, -1));
    }
  };

  const onPaste = (e) => {
    e.preventDefault();
    const bad = addEmail(e.clipboardData.getData("text"));
    setInput(bad.join(", "));
  };

  return (
    <div className={block.elem("wrap").toClassName()}>
      <div className={block.mod({ tall: !!tall }).toClassName()}>
        {emails.map((email) => (
          <span key={email} className={block.elem("chip").mod({ wide: !!renderChipExtra }).toClassName()}>
            <span className={block.elem("chip-text").toClassName()} title={email}>
              {email}
            </span>
            {renderChipExtra?.(email)}
            <button
              type="button"
              className={block.elem("chip-remove").toClassName()}
              onClick={() => onChange(emails.filter((e) => e !== email))}
              aria-label={`Remove ${email}`}
            >
              <IconClose style={{ width: 10, height: 10 }} />
            </button>
          </span>
        ))}
        <input
          className={block.elem("input").toClassName()}
          placeholder={emails.length ? "" : (placeholder ?? "Enter emails separated by comma or Enter…")}
          value={input}
          autoFocus={autoFocus}
          autoComplete="off"
          role={suggest ? "combobox" : undefined}
          aria-expanded={suggest ? open : undefined}
          aria-autocomplete={suggest ? "list" : undefined}
          onChange={(e) => {
            setInput(e.target.value);
            if (invalid.length) setInvalid([]);
          }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onFocus={() => options.length && setOpen(true)}
          onBlur={() => {
            setOpen(false);
            commitInput();
          }}
        />
      </div>
      {invalid.length > 0 && (
        <div className={block.elem("invalid").toClassName()} role="alert">
          Not a valid address (needs name@domain.tld): {invalid.join(", ")}
        </div>
      )}
      {open && options.length > 0 && (
        <div className={block.elem("menu").toClassName()} role="listbox">
          {options.map((o, i) => {
            const name = o.name || [o.first_name, o.last_name].filter(Boolean).join(" ");
            return (
              <div
                key={o.email}
                role="option"
                tabIndex={-1}
                aria-selected={i === active}
                className={block
                  .elem("option")
                  .mod({ active: i === active })
                  .toClassName()}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(o)}
              >
                <span className={block.elem("option-name").toClassName()}>{name || o.email}</span>
                {name && <span className={block.elem("option-email").toClassName()}>{o.email}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
